/**
 * O desenho dos gráficos que comparam LINHAS entre si — ranking e composição.
 *
 * Mora aqui, e não dentro de uma tela, porque dois tipos de lista desenham o mesmo: as telas das 32
 * métricas de envio e as que têm números próprios (cliques, leads). O que muda entre elas é de onde
 * sai o número; a barra, a escala e a legenda são as mesmas, e duas cópias delas sairiam do passo
 * uma da outra no primeiro ajuste.
 *
 * Estas peças recebem o número JÁ RESOLVIDO e JÁ ESCRITO: quem sabe ler a linha e formatar o valor
 * é quem chamou. Assim nada aqui precisa conhecer catálogo de métrica nenhum.
 */
import { useState, type ReactNode } from 'react';
import { VH, VW, niceMax, scaleY, ticks } from '@/components/v2/chart/geometry';
import { c, v, type Hue } from '@/components/v2/theme';

/** As maiores linhas que o ranking mostra; o resto fica na lista, abaixo. */
export const TOP_N = 12;
/** As maiores fatias que a composição nomeia; as outras entram somadas numa só. */
export const MIX_N = 8;
/** As cores das fatias, na ordem — cada uma mais apagada que a anterior. */
export const SLICE_HUE: Hue[] = ['good', 'cyan', 'violet', 'pink', 'amber', 'blue'];

/* ----------------------------------------------------------------- ranking */

export interface Bar {
    key: string;
    label: string;
    /** `null` = a linha não tem esse número. Vai para o fim e aparece como ausência, não como 0. */
    n: number | null;
    text: string;
    hue: Hue;
}

/**
 * Barras deitadas: nome de linha é longo, e em pé o rótulo não caberia.
 *
 * A escala é a maior barra, em módulo — receita negativa existe (estorno), e a barra dela tem de
 * ter tamanho.
 */
export function RankBars({ bars, count, what }: {
    bars: Bar[];
    /** Quantas linhas a lista tem, para dizer o que ficou de fora do ranking. */
    count: number;
    /** Como se chama uma linha desta lista, no plural: "linhas", "links". */
    what: string;
}) {
    const top = Math.max(1, ...bars.map((b) => Math.abs(b.n ?? 0)));
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {bars.map((b) => (
                <div key={b.key} style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                    <span title={b.label} style={{ fontSize: 12, color: v('text-3'), width: 190, flexShrink: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {b.label}
                    </span>
                    <span style={{ flex: 1, height: 10, borderRadius: 5, background: v('track'), minWidth: 0 }}>
                        <span style={{ display: 'block', height: '100%', width: `${(Math.abs(b.n ?? 0) / top) * 100}%`, borderRadius: 5, background: c(b.hue) }} />
                    </span>
                    <span className="rv-mono" style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums', width: 96, textAlign: 'right', color: b.n == null ? v('faint') : v('text') }}>
                        {b.text}
                    </span>
                </div>
            ))}
            {count > bars.length && (
                <span style={{ fontSize: 11, color: v('faint') }}>
                    {bars.length} de {count} {what} — a lista inteira fica abaixo
                </span>
            )}
        </div>
    );
}

/* ----------------------------------------------------------------- composição */

export interface Slice {
    key: string;
    label: string;
    n: number;
    hue: Hue;
    /** A fatia que junta o que não foi nomeado: desenhada apagada, para não parecer uma linha. */
    rest?: boolean;
}

/**
 * As fatias de uma composição, prontas: as maiores nomeadas e as outras somadas numa fatia só.
 *
 * Valor negativo não entra (vira 0): numa barra empilhada ele teria de ocupar espaço para a
 * esquerda, e não há para onde. Só número que SOMA pode chegar aqui — repartir uma taxa entre
 * linhas não quer dizer nada, e quem escolhe a medida é que garante isso.
 */
export const sliceUp = (parts: { key: string; label: string; n: number; hue?: Hue }[]) => {
    const all = [...parts].sort((a, b) => b.n - a.n);
    const head = all.slice(0, MIX_N);
    const rest = all.slice(MIX_N);
    const restSum = rest.reduce((a, x) => a + x.n, 0);
    const slices: Slice[] = [
        ...head.map((x, i) => ({ key: x.key, label: x.label, n: x.n, hue: x.hue ?? SLICE_HUE[i % SLICE_HUE.length] })),
        ...(rest.length ? [{ key: '_rest', label: `outras ${rest.length}`, n: restSum, hue: 'blue' as Hue, rest: true }] : []),
    ];
    return { slices, total: all.reduce((a, x) => a + x.n, 0) };
};

/** Uma barra empilhada e a legenda embaixo, com a fatia de cada linha no total. */
export function MixBar({ slices, total, pct, text }: {
    slices: Slice[];
    total: number;
    /** Como escrever a porcentagem — cada tela tem o seu formatador. */
    pct: (n: number) => string;
    /** Como escrever o valor da fatia. */
    text: (n: number) => string;
}) {
    const fill = (s: Slice, i: number) => c(s.hue, s.rest ? 0.25 : 1 - i * 0.07);
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', height: 16, borderRadius: 8, overflow: 'hidden', background: v('track') }}>
                {slices.map((s, i) => (
                    <span key={s.key} title={`${s.label}: ${text(s.n)}`}
                        style={{ width: `${(s.n / total) * 100}%`, background: fill(s, i) }} />
                ))}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,230px),1fr))', gap: '7px 16px' }}>
                {slices.map((s, i) => (
                    <div key={s.key} style={{ display: 'flex', alignItems: 'baseline', gap: 8, minWidth: 0, fontSize: 12 }}>
                        <span style={{ width: 8, height: 8, borderRadius: 2, flexShrink: 0, background: fill(s, i) }} />
                        <span title={s.label} style={{ color: v('text-3'), flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.label}</span>
                        <span className="rv-mono" style={{ fontVariantNumeric: 'tabular-nums', color: v('muted') }}>{pct((s.n / total) * 100)}</span>
                        <span className="rv-mono" style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{text(s.n)}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

/* ----------------------------------------------------------------- escolha da medida */

/** Os botões que escolhem o número do gráfico — a descrição vai no `title` de cada um. */
export function Pills<T extends { k: string; l: string; d: string }>({ opts, value, onChange }: {
    opts: T[];
    value: string;
    onChange: (k: string) => void;
}) {
    return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {opts.map((o) => {
                const on = o.k === value;
                return (
                    <button key={o.k} type="button" onClick={() => onChange(o.k)} title={o.d}
                        style={{
                            height: 26, padding: '0 10px', borderRadius: 7, cursor: 'pointer',
                            font: 'inherit', fontSize: 12, border: `1px solid ${on ? v('text') : v('border')}`,
                            background: on ? v('text') : 'transparent', color: on ? v('bg') : v('text-3'),
                        }}>
                        {o.l}
                    </button>
                );
            })}
        </div>
    );
}

/* ----------------------------------------------------------------- mapa de calor */

export interface HeatCol {
    k: string;
    l: string;
    d: string;
    /** Menos é melhor (bounce, descadastro): a cor inverte, senão diria o contrário do número. */
    down?: boolean;
}
export interface HeatRow { key: string; label: string }

/** Quanto a cor satura no extremo — acima disso o verde vira bloco e o número some dentro dele. */
const MAX_TONE = 0.42;

/**
 * Uma grade de números × linhas, cada célula pintada pela distância da MÉDIA das linhas.
 *
 * Três cuidados, e os três são de honestidade:
 * 1. a média sai das linhas que TÊM o número — contar ausência como zero puxaria a média para baixo
 *    e pintaria de verde quem só não está faltando;
 * 2. coluna de `down` inverte a cor: ali acima da média é pior;
 * 3. célula sem valor fica cinza com "—". Zero e ausência não podem ter a mesma cor.
 */
export function HeatGrid({ rows, cols, get, text, note }: {
    rows: HeatRow[];
    cols: HeatCol[];
    get: (row: HeatRow, col: HeatCol) => number | null;
    text: (col: HeatCol, n: number | null) => string;
    note: ReactNode;
}) {
    const avg = new Map<string, number | null>();
    for (const col of cols) {
        const vals = rows.map((r) => get(r, col)).filter((x): x is number => x != null);
        avg.set(col.k, vals.length ? vals.reduce((a, x) => a + x, 0) / vals.length : null);
    }
    const head = { fontSize: 10, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase' as const, color: v('faint') };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ overflowX: 'auto' }}>
                <div style={{ display: 'grid', gridTemplateColumns: `minmax(0,190px) repeat(${cols.length}, minmax(0,1fr))`, gap: 3, minWidth: 120 + cols.length * 86 }}>
                    <span />
                    {cols.map((col) => (
                        <span key={col.k} title={col.d} style={{ ...head, textAlign: 'center', padding: '0 4px 2px', lineHeight: 1.25 }}>
                            {col.l}
                        </span>
                    ))}
                    {rows.map((r) => (
                        <div key={r.key} style={{ display: 'contents' }}>
                            <span title={r.label} style={{ fontSize: 12, color: v('text-3'), whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'flex', alignItems: 'center', paddingRight: 8 }}>
                                {r.label}
                            </span>
                            {cols.map((col) => {
                                const n = get(r, col);
                                const mean = avg.get(col.k) ?? null;
                                const ratio = n != null && mean != null && mean !== 0 ? n / mean : null;
                                const up = ratio == null ? 0 : col.down ? 1 - ratio : ratio - 1;
                                const tone = Math.min(MAX_TONE, Math.abs(up) * 0.55);
                                const hue: Hue = up >= 0 ? 'good' : 'bad';
                                return (
                                    <span key={col.k}
                                        title={`${r.label} · ${col.l}: ${text(col, n)}${mean != null ? ` · média da lista ${text(col, mean)}` : ''}`}
                                        style={{
                                            height: 32, borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            fontSize: 11.5, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', padding: '0 4px',
                                            color: n == null ? v('faint') : v('text'),
                                            background: ratio == null ? v('inset') : c(hue, tone),
                                            border: `1px solid ${ratio == null ? v('border-soft') : c(hue, tone + 0.12)}`,
                                        }}>
                                        {text(col, n)}
                                    </span>
                                );
                            })}
                        </div>
                    ))}
                </div>
            </div>
            <p style={{ margin: 0, fontSize: 11, color: v('faint'), lineHeight: 1.5 }}>{note}</p>
        </div>
    );
}

/* ----------------------------------------------------------------- dispersão */

const R_MIN = 4;
const R_MAX = 22;

export interface Dot {
    key: string;
    label: string;
    x: number;
    y: number;
    /** O tamanho da bolha, na mesma unidade entre as linhas (envios, cliques). */
    size: number;
    hue?: Hue;
}

/**
 * Duas medidas, uma bolha por linha, o raio pela RAIZ do tamanho — proporcional ao tamanho, a linha
 * pequena viraria um ponto invisível ao lado de uma grande.
 *
 * Quem chama já tirou as linhas sem um dos dois números: uma bolha em (0,0) diria "deu zero", e
 * ausência não é zero. O rodapé é de quem chama, que sabe quantas ficaram de fora e por quê.
 */
export function ScatterPlot({ dots, xTick, yTick, tip, footer }: {
    dots: Dot[];
    xTick: (n: number) => string;
    yTick: (n: number) => string;
    tip: (d: Dot) => { l: string; val: string; col: string }[];
    footer: ReactNode;
}) {
    const [hover, setHover] = useState<string | null>(null);
    const mx = niceMax(Math.max(1, ...dots.map((q) => q.x)));
    const my = niceMax(Math.max(1, ...dots.map((q) => q.y)));
    const maxSize = Math.max(1, ...dots.map((q) => q.size));
    const y = scaleY(0, my);
    const yt = ticks(0, my, yTick);
    const xt = [0, 0.25, 0.5, 0.75, 1].map((f) => ({ f, l: xTick(mx * f) }));
    const r = (size: number) => R_MIN + (R_MAX - R_MIN) * Math.sqrt(Math.max(0, size) / maxSize);
    const on = dots.find((q) => q.key === hover);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ position: 'relative', height: 268 }}>
                <div style={{ position: 'absolute', left: 56, right: 14, top: 8, bottom: 30 }}>
                    {yt.map((t) => (
                        <span key={`l${t.top}`} style={{
                            position: 'absolute', right: 'calc(100% + 10px)', width: 48, textAlign: 'right',
                            top: `${t.top}%`, transform: 'translateY(-50%)', fontSize: 11, color: v('faint'),
                            fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
                        }}>{t.l}</span>
                    ))}
                    {yt.map((t) => (
                        <div key={`g${t.top}`} style={{ position: 'absolute', left: 0, right: 0, top: `${t.top}%`, borderTop: `1px dashed ${v('border-soft')}` }} />
                    ))}
                    {xt.map((t) => (
                        <div key={`x${t.f}`} style={{ position: 'absolute', top: 0, bottom: 0, left: `${t.f * 100}%`, borderLeft: `1px dashed ${v('border-soft')}` }} />
                    ))}
                    <svg viewBox={`0 0 ${VW} ${VH}`} preserveAspectRatio="none"
                        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', overflow: 'visible' }}>
                        {dots.map((q) => {
                            const lit = hover == null || hover === q.key;
                            return (
                                <circle key={q.key}
                                    cx={(q.x / mx) * VW} cy={y(q.y)} r={r(q.size)}
                                    fill={c(q.hue ?? 'cyan', lit ? 0.5 : 0.12)}
                                    stroke={c(q.hue ?? 'cyan', lit ? 1 : 0.2)} strokeWidth={1}
                                    vectorEffect="non-scaling-stroke"
                                    onMouseEnter={() => setHover(q.key)} onMouseLeave={() => setHover(null)}
                                    style={{ cursor: 'default' }}>
                                    <title>{q.label}</title>
                                </circle>
                            );
                        })}
                    </svg>
                    {on && (
                        <span style={{
                            position: 'absolute', left: `${(on.x / mx) * 100}%`, top: `${(y(on.y) / VH) * 100}%`,
                            transform: (on.x / mx) > 0.6 ? 'translate(calc(-100% - 18px), -50%)' : 'translate(18px, -50%)',
                            background: v('popover'), border: `1px solid ${v('border-3')}`, borderRadius: 10,
                            padding: '10px 12px', minWidth: 180, boxShadow: 'var(--rv-shadow)', pointerEvents: 'none',
                            zIndex: 3, display: 'flex', flexDirection: 'column', gap: 5, whiteSpace: 'nowrap',
                        }}>
                            <span style={{ fontSize: 12, color: v('muted'), maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis' }}>{on.label}</span>
                            {tip(on).map((row) => (
                                <span key={row.l} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
                                    <span style={{ width: 7, height: 7, borderRadius: 2, background: row.col, flexShrink: 0 }} />
                                    <span style={{ color: v('text-3'), flex: 1 }}>{row.l}</span>
                                    <span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{row.val}</span>
                                </span>
                            ))}
                        </span>
                    )}
                </div>
                <div style={{ position: 'absolute', left: 56, right: 14, bottom: 8, height: 16 }}>
                    {xt.map((t) => (
                        <span key={t.f} style={{ position: 'absolute', left: `${t.f * 100}%`, transform: 'translateX(-50%)', fontSize: 11, color: v('faint'), whiteSpace: 'nowrap' }}>
                            {t.l}
                        </span>
                    ))}
                </div>
            </div>
            <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', fontSize: 11.5, color: v('faint') }}>{footer}</div>
        </div>
    );
}
